import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Bolt, Lock, LogIn, Mail, ShieldCheck, KeyRound } from 'lucide-react';
import { GoogleSignIn } from '../features/auth/components/GoogleSignIn';
import ProtectedRoute from '../components/ProtectedRoute';

const credentialsSchema = z.object({
  email: z.string().trim().email('Enter a valid work email address'),
  password: z.string().min(1, 'Enter your password'),
});
type Credentials = z.infer<typeof credentialsSchema>;

export function AuthGate({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

export default function SignIn() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'signin' | 'signup'>(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [method, setMethod] = useState<'password' | 'magic-link'>('password');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });
  const email = watch('email');

  const submit = async ({ email, password }: Credentials) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      if (mode === 'signup') {
        if (password.length < 10) throw new Error('Use at least 10 characters for your new password.');
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        if (data.session) navigate('/onboarding', { replace: true });
        else setNotice('Account created. Check your email to confirm your address, then return here to sign in.');
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate('/workspace', { replace: true });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Authentication could not be completed.');
    } finally { setBusy(false); }
  };

  const sendMagicLink = async () => {
    if (!email || !z.string().email().safeParse(email).success) {
      setError('Enter a valid email address first.');
      return;
    }
    setBusy(true); setError(null); setNotice(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      setNotice('A secure sign-in link has been sent. Check your inbox and open it on this device.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not send the sign-in link.');
    } finally { setBusy(false); }
  };

  const resetPassword = async () => {
    if (!email || !z.string().email().safeParse(email).success) {
      setError('Enter your account email address first.');
      return;
    }
    setBusy(true); setError(null); setNotice(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (error) throw error;
      setNotice('If the account exists, a password-reset email will arrive shortly.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not request a password reset.');
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-page">
      <header className="auth-top">
        <Link to="/" className="brand" aria-label="Enerlectra home">
          <span className="brand-mark"><Bolt size={18} fill="currentColor" /></span>
          <span>ENERLECTRA</span><span className="brand-divider" />
          <span className="brand-sub">THE ENERGY INTERNET</span>
        </Link>
        <Link to="/" className="text-link"><ArrowLeft size={14} /> Back to home</Link>
      </header>

      <main className="auth-grid">
        <section className="auth-form-side">
          <div className="auth-form-box">
            <span className="eyebrow"><span className="live-dot" /> SECURE WORKSPACE ACCESS</span>
            <h1>{mode === 'signup' ? 'Create your account.' : 'Welcome back.'}</h1>
            <p>{mode === 'signup' ? 'Create your personal account first. You will set up your organization after email verification.' : 'Sign in to continue to your organization’s operational workspace.'}</p>

            <div className="auth-mode-switch" role="tablist" aria-label="Account access">
              <button type="button" role="tab" aria-selected={mode === 'signin'} className={mode === 'signin' ? 'active' : ''} onClick={() => { setMode('signin'); setError(null); setNotice(null); }}>Sign in</button>
              <button type="button" role="tab" aria-selected={mode === 'signup'} className={mode === 'signup' ? 'active' : ''} onClick={() => { setMode('signup'); setError(null); setNotice(null); }}>Create account</button>
            </div>

            {mode === 'signin' && <><div className="auth-divider"><span>CONTINUE WITH</span></div><GoogleSignIn /></>}

            <div className="auth-divider"><span>{mode === 'signup' ? 'REGISTER WITH EMAIL' : method === 'password' ? 'USE YOUR EMAIL AND PASSWORD' : 'PASSWORDLESS EMAIL ACCESS'}</span></div>
            {mode === 'signin' && <div className="auth-method-switch" role="group" aria-label="Email sign-in method">
              <button type="button" className={method === 'password' ? 'active' : ''} onClick={() => { setMethod('password'); setError(null); setNotice(null); }}><Lock size={14}/> Password</button>
              <button type="button" className={method === 'magic-link' ? 'active' : ''} onClick={() => { setMethod('magic-link'); setError(null); setNotice(null); }}><Mail size={14}/> Magic link</button>
            </div>}

            <form onSubmit={handleSubmit(submit)} className="form-stack">
              <label className="field-wrap">
                <span className="field-label">Work email</span>
                <span className="auth-input-wrap"><Mail size={16} aria-hidden="true"/><input {...register('email')} type="email" autoComplete="email" className="field auth-input" placeholder="you@company.com" disabled={busy}/></span>
                {errors.email && <span className="field-error">{errors.email.message}</span>}
              </label>
              {(mode === 'signup' || method === 'password') && <label className="field-wrap">
                <span className="field-label">{mode === 'signup' ? 'Create password' : 'Password'}</span>
                <span className="auth-input-wrap"><Lock size={16} aria-hidden="true"/><input {...register('password')} type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} className="field auth-input" placeholder={mode === 'signup' ? 'At least 10 characters' : 'Enter your password'} disabled={busy}/></span>
                {errors.password && <span className="field-error">{errors.password.message}</span>}
              </label>}
              {error && <p className="auth-error" role="alert">{error}</p>}
              {notice && <p className="auth-notice" role="status">{notice}</p>}
              {method === 'magic-link' && mode === 'signin'
                ? <button type="button" disabled={busy} onClick={sendMagicLink} className="button button-dark full-button auth-submit"><KeyRound size={16}/> {busy ? 'Sending secure link…' : 'Email me a sign-in link'}</button>
                : <button type="submit" disabled={busy} className="button button-dark full-button auth-submit"><LogIn size={16}/> {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Access workspace'}</button>}
            </form>
            {mode === 'signin' && method === 'password' && <button type="button" className="auth-text-action" onClick={resetPassword} disabled={busy}>Forgot your password?</button>}
            <p className="fine-print">Your organization’s data is isolated by verified membership and server-side permissions.</p>
          </div>
        </section>

        <aside className="auth-aside">
          <div>
            <span className="eyebrow">ENERLECTRA / OPERATIONS</span>
            <div className="auth-aside-content">
              <span className="aside-icon"><Bolt size={25} fill="currentColor" /></span>
              <h2>Evidence before action.</h2>
              <p>Context informs recommendations. Authorization controls work. Verification establishes outcomes.</p>
              <div className="auth-principles"><span>01 <b>REPORT</b></span><span>02 <b>UNDERSTAND</b></span><span>03 <b>AUTHORIZE</b></span><span>04 <b>VERIFY</b></span></div>
            </div>
          </div>
          <div className="auth-aside-foot"><ShieldCheck size={15}/> PRIVATE ORGANIZATION WORKSPACES · EXPLICIT AUTHORITY · VERIFIED OUTCOMES</div>
        </aside>
      </main>
    </div>
  );
}
